import type { FC, ReactElement } from "react";
import type React from "react";
import { useState } from "react";

import { useDispatch } from "react-redux";

import { shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import { FlexRow, Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import { OPTION_MENU } from "app/pages/sales-intelligence/common-components/options-menu/items";
import type { MenuItemOption } from "app/pages/sales-intelligence/common-components/options-menu/types";
import useDeactivateOnOutsideClick from "app/pages/sales-intelligence/hooks/useDeactivateOnOutsideClick";
import type { SavedSearchesDto } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/types/dto";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { deleteSavedSearchModalToggledAction } from "app/pages/sales-intelligence/sub-modules/advanced-search/store/action-creators";
import dateTimeService from "app/services/date-time/dateTimeService";
import type { ThunkDispatchCommon } from "single-spa/store/types";

import { NewItemsButton } from "../NewItemsButton/NewItemsButton";

import {
    StyledCustomIcon,
    StyledListItemMainData,
    StyledSavedSearchItemContainer,
    StyledListLastUpdatedDate,
    StyledOptionsMenu,
    StyledEditableText,
} from "./styles";

const CLASSNAME = "saved-search-list-item";

interface ISavedSearchItemProps {
    item: SavedSearchesDto;
    onRename: (item: SavedSearchesDto, name: string) => void;
}

export const SavedSearchItem: FC<ISavedSearchItemProps> = ({ item, onRename }): ReactElement => {
    const [isNameBeingEdited, setIsNameBeingEdited] = useState(false);
    const dispatch: ThunkDispatchCommon = useDispatch();

    useDeactivateOnOutsideClick(CLASSNAME, isNameBeingEdited, () => setIsNameBeingEdited(false));

    const handleOnClick = (id: string) => {
        if (!isNameBeingEdited) {
            siNavigator.goToSavedSearchPageById(
                {
                    savedSearchId: id,
                },
                { reload: true },
            );
        }
    };

    const handleNewWebsitesClick = (e: React.MouseEvent<HTMLDivElement>, id: string) => {
        e.stopPropagation();
        if (!isNameBeingEdited) {
            siNavigator.goToSavedSearchPageById(
                {
                    savedSearchId: id,
                    isNewOnly: true,
                },
                { reload: true },
            );
        }
    };

    const handleMenuItemSelect = (option: MenuItemOption, list: Readonly<SavedSearchesDto>) => {
        const { name, totalCount, id, newCount } = list;
        switch (option) {
            case "rename":
                return setIsNameBeingEdited(true);
            case "delete":
                return dispatch(
                    deleteSavedSearchModalToggledAction({
                        name,
                        totalNumberOfWebsites: totalCount,
                        searchId: id,
                        numberOfNewWebsites: newCount,
                    }),
                );
            default:
                return;
        }
    };

    const { name, id, lastUpdated, newCount } = item;

    const handleOnEditTextSubmit = async (newName: string) => {
        await onRename(item, newName);
        setIsNameBeingEdited(false);
    };

    return (
        <StyledSavedSearchItemContainer
            className={CLASSNAME}
            onClick={() => handleOnClick(id)}
        >
            <StyledCustomIcon iconName="search-radar" />
            <StyledListItemMainData>
                <FlexRow>
                    <StyledEditableText
                        text={name}
                        maxInputWidth={100}
                        iconName={"edit-icon-fill"}
                        isBeingEdited={isNameBeingEdited}
                        onSubmit={handleOnEditTextSubmit}
                        onCancel={() => setIsNameBeingEdited(false)}
                    />
                </FlexRow>
                {lastUpdated ? (
                    <I18n
                        translatable={{
                            key: "si.my-list.list-item.last-updated",
                            replacements: {
                                date: dateTimeService.formatWithMoment(lastUpdated, shortMonthDayAndYear),
                            },
                        }}
                        element={StyledListLastUpdatedDate}
                    />
                ) : null}
            </StyledListItemMainData>
            <Tooltip
                content={translate("si.advanced_search.my-saved-searches.list.item.new-results.tooltip")}
                placement={"top"}
            >
                <div>
                    <NewItemsButton
                        text={translate("si.advanced_search.my-saved-searches.list.item.new-results")}
                        count={newCount}
                        onClick={(e: React.MouseEvent<HTMLDivElement>) => handleNewWebsitesClick(e, id)}
                    />
                </div>
            </Tooltip>

            <StyledOptionsMenu
                id={id}
                key={id}
                items={OPTION_MENU}
                onSelect={(option) => handleMenuItemSelect(option, item)}
                tooltipContent={translate("si.common.option-menu.placeholder")}
            />
        </StyledSavedSearchItemContainer>
    );
};
